import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { Url } from 'next/dist/shared/lib/router/router';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import Button from '~/components/Button';
import { QUERY_GET_RELEASE_PRODUCT } from '~/components/SneakerRelease/graphql';

import { PATH } from '~/constants/enum';
import { formatNumber } from '~/utils/number';
import ShiftingCountDown from '../../CountDown';
import { SneakLaunch } from '~/services/sneaker-release/sneaker-release.type';
import { transformReleaseProduct } from '~/services/sneaker-release/sneaker-release-transform';
import { getContrastingTextColor } from '~/utils/helper';

type ProductItemLaunchProps = {
    product?: SneakLaunch;
    onClick?: () => void;
    loading?: boolean;
    footer?: React.ReactNode;
    skip?: boolean;
};

const ProductItemLaunch = ({ product, onClick, loading = false, footer, skip }: ProductItemLaunchProps) => {
    const { data: fetchReleaseProduct, loading: actualLoading } = useQuery<any>(QUERY_GET_RELEASE_PRODUCT, {
        variables: {
            slug: product?.slug
        },
        notifyOnNetworkStatusChange: true,
        skip: skip
    });
    const router = useRouter();

    const { items = [] } = fetchReleaseProduct?.getReleaseProducts || {};

    const actualProduct: SneakLaunch = useMemo(() => {
        if (!!skip && !!product) {
            return product;
        }
        return {
            ...product,
            ...transformReleaseProduct(items[0])
        };
    }, [product, items, skip]);

    const { title, slug, retail, thumbnail, pageTheme, releaseDate, publishDate, color, brandCode } =
        actualProduct || {};

    const href: Url = {
        pathname: PATH.SneakerReleaseDetail.replace('[brand]', brandCode).replace('[slug]', slug)
    };

    const finalLoading = loading || actualLoading;
    const hrefFindMatch = useMemo(() => {
        if (product?.tags) {
            return PATH.CollectionDetail.replaceAll('[slug]', product?.tags);
        } else {
            return PATH.Search + '?keyword=' + title;
        }
    }, [product?.tags, title]);

    return (
        <article
            className={`col-span-full md:col-span-6 xl:col-span-3 h-full flex flex-col
                 relative bg-white`}
        >
            <div
                className="border border-solid"
                onClick={onClick}
                style={{
                    backgroundColor: pageTheme,
                    color: getContrastingTextColor(pageTheme || ''),
                    borderColor: pageTheme === 'rgb(255, 255, 255)' ? pageTheme : undefined
                }}
            >
                <Link
                    className="block relative w-full"
                    href={href}
                    onClick={finalLoading ? (e) => e.preventDefault() : undefined}
                >
                    <div className="relative w-full h-[302px]">
                        <div className="h-full relative w-full block overflow-hidden">
                            {finalLoading ? (
                                <Skeleton
                                    className="h-full !rounded-none !leading-[unset]"
                                    containerClassName="opacity-1 absolute w-full h-full inset-0 object-contain"
                                />
                            ) : (
                                <picture>
                                    {thumbnail?.root && (
                                        <img
                                            className="opacity-1 absolute w-full h-full inset-0 object-contain"
                                            src={thumbnail?.root}
                                            loading="lazy"
                                        />
                                    )}
                                </picture>
                            )}
                        </div>
                    </div>
                </Link>
            </div>
            <div className="grow flex flex-col relative overflow-hidden">
                <div
                    className={classNames(
                        'relative z-1 flex md:p-4 p-2 flex-col grow border border-t-0 justify-between'
                    )}
                >
                    <div className="mb-4 flex-1 flex flex-col justify-between">
                        {finalLoading ? (
                            <>
                                <Skeleton className="h-6" />
                            </>
                        ) : (
                            <Link className="flex text-md xl:leading-[1.35] mb-1" href={href}>
                                <div className="block !line-clamp-2 md:text-xl text-lg" onClick={onClick}>
                                    {title}
                                </div>
                            </Link>
                        )}
                        <div className="flex items-end justify-between mt-1 gap-10">
                            <div className="flex gap-2 text-gray-500">
                                {finalLoading ? <Skeleton className="h-6 w-60" /> : <span>{color ?? 'Updating'}</span>}
                            </div>
                            <div className="flex gap-2 flex-1 justify-end">
                                {finalLoading ? (
                                    <Skeleton className="h-6 w-20" />
                                ) : (
                                    <span>{!!Number(retail) ? `$${formatNumber(Number(retail), 2)}` : `Updating`}</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className={`flex flex-col`}>
                        {footer ? (
                            footer
                        ) : footer !== false ? (
                            <div>
                                {finalLoading ? (
                                    <Skeleton className="h-10" />
                                ) : (
                                    <Link href={hrefFindMatch} target="_blank" className="w-full">
                                        <Button
                                            size="medium"
                                            type="outline"
                                            className="w-full"
                                            // onClick={handleFindMatchingShirt}
                                        >
                                            Find A Matching Shirt
                                        </Button>
                                    </Link>
                                )}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
            {!finalLoading && (
                <div className="flex gap-2 absolute top-2 right-2">
                    <ShiftingCountDown
                        from={releaseDate}
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            color: getContrastingTextColor(pageTheme || '')
                        }}
                    />
                </div>
            )}
        </article>
    );
};

export default ProductItemLaunch;
